import React, { useMemo } from 'react';
import CheckCircle from '@mui/icons-material/CheckCircleOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { type components } from '../../../../endpoint/Schema';
import useTenantStore from '../../../../stores/TenantStore';
import { ReactComponent as EditIcon } from '../../../../assets/icons/ic_edit.svg';

interface IMyAccordionProps {
  title: string;
  appFontType: components['schemas']['AppFontType'];
  index: number;
  expandedIndex?: number;
  onToggle: (index: number) => void;
}

const MyAccordion: React.FC<React.PropsWithChildren<IMyAccordionProps>> = ({
  title,
  appFontType,
  index,
  expandedIndex,
  onToggle,
  children,
}) => {
  const appFonts = useTenantStore((state) => state.appFonts);
  const hasImage = useMemo(
    () => appFonts?.some((a) => a.name === appFontType),
    [appFonts, appFontType],
  );
  const isExpanded = expandedIndex === index;

  function toggleCollapsed() {
    onToggle(index);
  }

  return (
    <div
      className={`border rounded-2xl mb-4 ${hasImage ? 'border-success bg-s50' : 'border-primary bg-white'}`}
    >
      <button
        className="flex gap-5 justify-between items-center w-full max-md:max-w-full max-md:gap-3 px-8 py-4 max-md:px-4"
        onClick={toggleCollapsed}
      >
        <div className="flex gap-3 items-center max-md:gap-2">
          {hasImage && (
            <CheckCircle className="text-primary" sx={{ fontSize: '2rem' }} />
          )}
          <div className="self-stretch my-auto text-xl leading-8 text-black text-left">
            {title}
          </div>
        </div>

        {hasImage && !isExpanded ? (
          <EditIcon className="w-[2rem] aspect-square fill-primary stroke-primary" />
        ) : (
          <div
            className={`shrink-0 my-auto text-darkGray transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
          >
            <ExpandMore sx={{ fontSize: '2rem' }} />
          </div>
        )}
      </button>

      <div
        className={`overflow-hidden transition-[max-height] px-[2rem] duration-300 ${isExpanded ? 'max-h-[1000rem]' : 'max-h-0'}`}
      >
        <div className="py-[1rem]">{children}</div>
      </div>
    </div>
  );
};

export default MyAccordion;
